import {
	ReactElement,
	useMemo
} from "react";
import { EventList } from "./EventList/EventList";
import {
	Appointment,
	DateToDisplay,
	getAppointmentsForDay
} from "./CalendarLiveTileContent";
import { newGuid } from "client_legacy/ndaw/guid";
import { unique } from "client_legacy/ndaw/arrayHelpers";
import { TimeZone } from "client/DateAndTime/timezones";
import { addDays } from "client/DateAndTime/dateTimeUtils";
import { useToday } from "client/Hooks";
import { CalendarDestinationApp, DestinationWindow } from "client/Api/Apps/common";
import { AppointmentItemModel } from "client_legacy/Workspace/LiveInformation/Calendar/provider";

export type UpcomingEventsViewProps = {
	upcomingAppointments: readonly AppointmentItemModel[];
	errorMessage: string | nil;
	timezone: TimeZone;
	destinationApp: CalendarDestinationApp;
	destinationWindow: DestinationWindow;
	onListItemClick(): void;
	calendarId: string;
};

export const UpcomingEventsView = (props: UpcomingEventsViewProps): ReactElement => {
	const {
		upcomingAppointments,
		errorMessage,
		timezone,
		destinationApp,
		destinationWindow,
		onListItemClick,
		calendarId
	} = props;

	const today: Date = useToday({ timezone });

	const datesToDisplay = useMemo(
		(): readonly DateToDisplay[] => convertAppointmentsToDatesToDisplay(
			unique(
				upcomingAppointments,
				x => x.uniqueId
			),
			today,
			timezone
		),
		[
			upcomingAppointments,
			timezone,
			today
		]
	);

	return (
		<EventList
			datesToDisplay={datesToDisplay}
			errorMessage={errorMessage}
			trackingId="upcoming-events-list"
			hasShortJoinButton
			timezone={timezone}
			destinationApp={destinationApp}
			destinationWindow={destinationWindow}
			onListItemClick={onListItemClick}
			calendarId={calendarId}
		/>
	);
};
UpcomingEventsView.displayName = "UpcomingEventsView";

const NUMBER_OF_DAYS_TO_LOOK_FORWARD: number = 30;

function convertAppointmentsToDatesToDisplay(
	appointments: readonly AppointmentItemModel[],
	today: Date,
	timezone: TimeZone
): DateToDisplay[] {
	const result: DateToDisplay[] = [];

	for (let dayNumber = 0; dayNumber < NUMBER_OF_DAYS_TO_LOOK_FORWARD; dayNumber++) {
		const day: Date = addDays(
			today,
			dayNumber,
			timezone
		);

		const appointmentModelsPerDay: Appointment[] = getAppointmentsForDay(appointments, day, timezone);

		result.push({
			uniqueKey: newGuid(),
			date: {
				isToday: dayNumber === 0,
				value: day
			},
			appointments: appointmentModelsPerDay
		});
	}

	return result;
};
